import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, HTMLAttributes } from 'react';

import { cn } from '../layout';

export type AgentWorkbenchMessageRole = 'assistant' | 'system' | 'user';

export interface AgentWorkbenchMessageProps extends HTMLAttributes<HTMLDivElement> {
  role: AgentWorkbenchMessageRole;
}

export interface AgentWorkbenchMessageContentProps extends HTMLAttributes<HTMLDivElement> {}

export interface AgentWorkbenchMessageActionsProps extends HTMLAttributes<HTMLDivElement> {}

export interface AgentWorkbenchMessageActionProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  tooltip?: string;
}

export const AgentWorkbenchMessage = forwardRef<HTMLDivElement, AgentWorkbenchMessageProps>(
  function AgentWorkbenchMessage({ role, className, ...messageProps }, ref) {
    return (
      <div
        {...messageProps}
        ref={ref}
        data-agent-message-role={role}
        className={cn(
          'group flex w-full max-w-[95%] min-w-0 flex-col gap-2',
          role === 'user' ? 'is-user ml-auto justify-end' : 'is-assistant',
          role === 'system' && 'mx-auto items-center text-center',
          className,
        )}
      />
    );
  },
);

export const AgentWorkbenchMessageContent = forwardRef<
  HTMLDivElement,
  AgentWorkbenchMessageContentProps
>(function AgentWorkbenchMessageContent({ children, className, tabIndex = 0, ...contentProps }, ref) {
  return (
    <div
      {...contentProps}
      ref={ref}
      tabIndex={tabIndex}
      className={cn(
        'flex w-fit min-w-0 max-w-full flex-col gap-2 overflow-x-auto overflow-y-hidden break-words text-sm [overflow-wrap:anywhere]',
        '[&_a]:max-w-full [&_a]:break-all [&_code]:max-w-full [&_code]:break-words',
        '[&_pre]:w-max [&_pre]:min-w-full [&_pre]:whitespace-pre [&_pre_code]:break-normal',
        '[&_table]:w-max [&_table]:min-w-full [&_table]:whitespace-nowrap',
        'group-[.is-user]:ml-auto group-[.is-user]:rounded-lg group-[.is-user]:bg-secondary group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground',
        'group-[.is-assistant]:text-foreground',
        className,
      )}
    >
      {children}
    </div>
  );
});

export const AgentWorkbenchMessageActions = forwardRef<
  HTMLDivElement,
  AgentWorkbenchMessageActionsProps
>(function AgentWorkbenchMessageActions({ children, className, ...actionsProps }, ref) {
  return (
    <div {...actionsProps} ref={ref} className={cn('flex min-w-0 items-center gap-1', className)}>
      {children}
    </div>
  );
});

export const AgentWorkbenchMessageAction = forwardRef<
  HTMLButtonElement,
  AgentWorkbenchMessageActionProps
>(function AgentWorkbenchMessageAction(
  { children, label, tooltip, className, disabled = false, type = 'button', ...buttonProps },
  ref,
) {
  return (
    <button
      {...buttonProps}
      ref={ref}
      type={type}
      aria-label={label}
      title={tooltip}
      disabled={disabled}
      className={cn(
        'inline-flex size-8 shrink-0 items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground shadow-none transition-colors',
        'hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
        className,
      )}
    >
      {children}
    </button>
  );
});
